/**
 * Data helpers for the CSI-Tipalti Analysis screen (AB#6872): reading rows from the API,
 * building the review save body, tri-state filters, paging and dates.
 *
 * PURE module: no React, MUI, axios or auth imports, so it is unit-testable.
 */
import type {
  CSITipaltiStaging,
  UpdateCSITipaltiBody,
} from "../../types/CSITypes";
import { EMPTY_VALUE, formatDate } from "../../utils/format";

const str = (value: unknown): string =>
  typeof value === "string" ? value : "";

const strOrNull = (value: unknown): string | null =>
  typeof value === "string" && value !== "" ? value : null;

const boolOrNull = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null;

const count = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const ids = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v) => typeof v === "string") : [];

/**
 * One API row as a CSITipaltiStaging. Values are kept exactly as received; only absent
 * values get an empty default (never today's date, never a masked tax ID), because this
 * object is what a review save posts back.
 */
export function toStagingRow(item: unknown): CSITipaltiStaging {
  const raw = (item !== null && typeof item === "object" ? item : {}) as Record<
    string,
    unknown
  >;
  return {
    id: str(raw.id),
    payeeId: str(raw.payeeId),
    tin: str(raw.tin),
    groupPayeeIds: ids(raw.groupPayeeIds),
    matchedPayeeId: strOrNull(raw.matchedPayeeId),
    additionalMatchedPayeeIds: ids(raw.additionalMatchedPayeeIds),
    csiPaymentMethod: str(raw.csiPaymentMethod),
    csiCreatedDate: strOrNull(raw.csiCreatedDate),
    csiName: str(raw.csiName),
    csiEmail: str(raw.csiEmail),
    csiAddress: str(raw.csiAddress),
    tipaltiName: str(raw.tipaltiName),
    tipaltiEmail: str(raw.tipaltiEmail),
    tipaltiAddress: str(raw.tipaltiAddress),
    tipaltiPaymentMethod: str(raw.tipaltiPaymentMethod),
    inTipalti: boolOrNull(raw.inTipalti),
    isMatchedWithTipalti: boolOrNull(raw.isMatchedWithTipalti),
    matchBasis: str(raw.matchBasis),
    migratedBills: count(raw.migratedBills),
    nonMigratedBills: count(raw.nonMigratedBills),
    analysisCreatedOn: strOrNull(raw.analysisCreatedOn),
    analysisCompletedOn: strOrNull(raw.analysisCompletedOn),
    analysisCompletedBy: str(raw.analysisCompletedBy),
    isPayable: boolOrNull(raw.isPayable),
    payableReason: str(raw.payableReason),
    memo: typeof raw.memo === "string" ? raw.memo : null,
    lastModified: strOrNull(raw.lastModified),
    lastModifiedBy: strOrNull(raw.lastModifiedBy),
    reviewedResults: strOrNull(raw.reviewedResults),
    // The server treats a missing IsActive as active.
    isActive: typeof raw.isActive === "boolean" ? raw.isActive : true,
  };
}

/** What a review changes on a row. `reviewedResults` is a stored string or '' (None). */
export interface ReviewChange {
  memo: string;
  reviewedResults: string;
  isActive: boolean;
}

/** When and by whom (the server overwrites both with its own clock and the signed-in user). */
export interface ModifiedStamp {
  at: string;
  by: string;
}

/**
 * Body for POST .../CSITipaltiStagings/{id}/update: the FULL row with the review fields
 * replaced, exactly as the screen has always sent it (the server maps every field onto
 * the record). A date the response did not have is left out rather than sent as null,
 * which the server cannot read into its non-nullable date.
 */
export function buildReviewPayload(
  row: CSITipaltiStaging,
  change: ReviewChange,
  stamp: ModifiedStamp
): UpdateCSITipaltiBody {
  const { csiCreatedDate, analysisCreatedOn, ...rest } = row;
  return {
    ...rest,
    ...(csiCreatedDate !== null ? { csiCreatedDate } : {}),
    ...(analysisCreatedOn !== null ? { analysisCreatedOn } : {}),
    memo: change.memo,
    reviewedResults: change.reviewedResults,
    isActive: change.isActive,
    lastModified: stamp.at,
    lastModifiedBy: stamp.by,
  };
}

/** The row as it reads after a successful save (what a reload would show). */
export function applyReview(
  row: CSITipaltiStaging,
  change: ReviewChange,
  stamp: ModifiedStamp
): CSITipaltiStaging {
  return {
    ...row,
    memo: change.memo,
    reviewedResults: change.reviewedResults || null,
    isActive: change.isActive,
    lastModified: stamp.at,
    lastModifiedBy: stamp.by || null,
  };
}

/** Tri-state filter value: null = Any, true = Yes, false = No. */
export type TriState = boolean | null;

/** Any -> Yes -> No -> Any. */
export function nextTriState(value: TriState): TriState {
  if (value === null) return true;
  if (value === true) return false;
  return null;
}

export function triStateLabel(value: TriState): "Any" | "Yes" | "No" {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Any";
}

/** The "All" rows-per-page option. */
export const ALL_ROWS = -1;

export const ROWS_PER_PAGE_OPTIONS: (
  | number
  | { label: string; value: number }
)[] = [10, 25, 50, 100, 500, 1000, { label: "All", value: ALL_ROWS }];

/**
 * Page size to request. "All" asks for every matching row: the last known total
 * (at least 1, as the server turns anything below 1 into 10). If the total has grown
 * since, the caller fetches again with the new total.
 */
export function effectivePageSize(
  rowsPerPage: number,
  knownTotal: number
): number {
  if (rowsPerPage !== ALL_ROWS) return rowsPerPage;
  return Math.max(knownTotal, 1);
}

/** Parses a date the API sent; missing, invalid or placeholder (year < 1900) -> null. */
export function toRealDate(
  value: string | Date | null | undefined
): Date | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime()) || date.getFullYear() < 1900) return null;
  return date;
}

/** MM/dd/yyyy, or "—" when there is no real date. */
export function formatStagingDate(value: string | null | undefined): string {
  const date = toRealDate(value);
  return date ? formatDate(date) : EMPTY_VALUE;
}

/** e.g. "Oct 1, 2026, 9:42 AM", or "—" when there is no real date. */
export function formatStagingDateTime(
  value: string | null | undefined,
  opts?: { timeZone?: string }
): string {
  const date = toRealDate(value);
  if (!date) return EMPTY_VALUE;
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: opts?.timeZone,
  }).format(date);
}

/** Rows on the current page that carry exactly this tax ID (none for an empty value). */
export function rowsWithSameTaxId(
  rows: CSITipaltiStaging[],
  value: string | null | undefined
): CSITipaltiStaging[] {
  if (!value) return [];
  return rows.filter((row) => row.tin === value);
}

/** "First Middle Last" from a payee name object, skipping empty parts. */
export function fullName(
  name:
    | { first?: string | null; middle?: string | null; last?: string | null }
    | null
    | undefined
): string {
  return [name?.first, name?.middle, name?.last]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
}
